<template lang="pug">
div.neutral200
  v-container.pa-8(style="max-width:1100px")
    div.d-flex.justify-center.mt-16(v-if="isFetching")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

    template(v-else)
      v-row(no-gutters align="center")
        v-col.shrink
          v-btn.ml-n3(text icon @click="goBack" height)
            v-icon(color="basic800" :size="24") mdi-arrow-left
        v-col.ml-3
          .text-2xl.font-semibold.neutral700--text {{ id ? 'Edit location' : 'Add location' }}

      v-form(ref="form")
        v-card.pa-6.mt-4.round-xl(outlined)
          .text-md.font-semibold.neutral700--text Location details
          v-row.mt-3
            v-col(cols="6")
              div.text-sm.font-semibold.neutral500--text Name
              v-text-field.text-sm.mt-2.round-xl(v-model="locationName" :placeholder="$t('name')" outlined dense height="48" :rules="rules.not_empty" hide-details="auto")

        v-card.pa-6.mt-4.round-xl(outlined)
          .text-md.font-semibold.neutral700--text Add item to this location
          div.d-flex.align-center.mt-2
            .text-sm.neutral700--text.mr-1(style="margin-top: 2px;") Search items you want to add to this location from your inventory list.

          v-autocomplete.text-sm.round-xl.mt-6.autocmplt(ref="autocmplt" v-model="product" :items="productList" item-text="name" return-object :search-input.sync="searchProducts" placeholder="Search and select here" :loading="isSearching" outlined dense clearable height="48" hide-details="auto" hide-no-data attach append-icon="" @change="addToList(product)" @blur="clearAutocomplete")
            template(#prepend-inner)
              v-icon.mt-1(size="22") $yzi-search-sm
            template(#item="{ index, item }")
              div.py-3.px-2
                .text-sm.font-normal(v-if="item.uom") {{ item.name }}  ({{item.uom}})
                .text-sm.font-normal(v-else) {{ item.name }}
                .d-flex
                  .d-flex.align-center.mr-4
                    template(v-if="hasStock(item)")
                      v-icon.mr-1(size="11" color="success") $yzi-circle
                      .text-xs.font-normal.neutral500--text {{ $t('in_stock') }}
                    template(v-else)
                      v-icon.mr-1(size="11" color="danger") $yzi-circle
                      .text-xs.font-normal.neutral500--text {{ $t('out_of_stock') }}
                  .d-flex.align-center.mr-4
                    v-icon.mr-1(size="13") {{ getTypeIcon(item) }}
                    .text-xs.font-normal.neutral500--text {{ item.type | capitalize }}
                  template(v-if="item.groups && item.groups.length > 0")
                    .d-flex.align-center.mr-4
                      v-icon.mr-1(size="13") $yzi-folder
                      .text-xs.font-normal.neutral500--text {{ item.groups.join(', ') | capitalize }}

          v-data-table.mt-2(:headers="tableHeaders" :items="products" item-key="id" :items-per-page="-1" :hide-default-header="products.length === 0" hide-default-footer disable-sort :loading="isSelecting" :header-props="{ sortIcon: '$yzi-triangle' }")
            template(#no-data)
              div.empty-box.mt-6.pa-8(align="center")
                v-avatar(color="neutral300" size="54")
                  v-icon(color="neutral500" size="30") $yzi-colors
                div.text-center.text-md.font-semibold.neutral700--text.mt-6 No inventory selected
                div.text-center.text-sm.font-normal.neutral500--text.mt-1 Selected inventory will be displayed here
            template(v-slot:item.number="{ item, index }")
              div.py-2
                .text-sm.neutral700--text {{ index+1 }}
            template(v-slot:item.name="{ item }")
              div.py-2
                .text-sm.neutral700--text {{ item.name }}
            template(v-slot:item.uom="{ item }")
              div.py-2
                .text-sm.neutral700--text(v-if="item.uom") {{ item.uom }}
                .text-sm.neutral700--text(v-else) -
            template(v-slot:item.action="{ item }")
              div.d-flex.justify-end
                v-btn(icon small @click="deleteProduct(item)")
                  v-icon $yzi-trash-03

    v-footer.white(app inset padless elevation="3")
      v-container.px-8.py-2
        div.d-flex.align-center
          v-spacer
          y-button.mr-4(:label="$t('cancel')" underline color="neutral700" size="md" button-class="px-0" @click="goBack")
          y-button(label="Save" color="neutral700" dark :loading="isLoading" @click="onSave")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'HQStorageLocation',
  props: ['id'],
  data () {
    return {
      isLoading: false,
      isFetching: false,
      isSearching: false,
      isSelecting: false,
      searchProducts: null,
      product: null,
      locationName: null,
      products: [],
      productList: [],
      tableHeaders: [
        { text: '#', value: 'number', class: 'text-table-header' },
        { text: 'Name', value: 'name', class: 'text-table-header' },
        { text: 'UOM', value: 'uom', class: 'text-table-header' },
        { text: '', value: 'action', class: 'text-table-header' },
      ],
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    warehouseSlug () {
      return this.$route.query.warehouse
    },
    warehouseId () {
      return this.$route.query.warehouseId
    },
  },
  watch: {
    searchProducts (val) {
      if (val) {
        this.fetchProducts(val)
      }
    }
  },
  mounted () {
    if (this.id) {
      this.fetchLocation()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchLocation () {
      if (!this.warehouseSlug) return
      this.isFetching = true
      this.$axios.get(`/clinics/${this.warehouseSlug}/inventories/locations/${this.id}?expand=products`)
        .then(resp => {
          this.products = resp.data.products
          this.locationName = resp.data.value
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchProducts (search) {
      if (!this.warehouseSlug) return
      this.isSearching = true
      const params = {
        relations: 'medication',
        status: 'published',
        ordering: '-inventory_qty,name'
      }
      if (search) {
        params.search = search
      }
      this.$axios.get(`/clinics/${this.warehouseSlug}/treatment_products`, { params })
        .then(resp => {
          this.productList = resp.data.results
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isSearching = false
        })
    },
    addToList (product) {
      if (product) {
        if (this.products.find(f => f.id === product.id)) {
          return
        }
        this.products.push(product)
        this.$refs.autocmplt.blur()
      }
    },
    onSave () {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      if (!this.products.length) {
        this.showMessage(['error', 'Please attach inventory and services'])
        return
      }
      this.isLoading = true

      if (this.id) {
        const data = {
          value: this.locationName,
          products: this.products.map(m => m.id)
        }
        this.$axios.patch(`/clinics/${this.warehouseSlug}/inventories/locations/${this.id}`, data)
          .then(() => {
            this.goBack()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        const data = {
          company: parseInt(this.warehouseId),
          field: 'medication.location',
          value: this.locationName,
          products: this.products.map(m => m.id)
        }
        this.$axios.post(`/clinics/${this.warehouseSlug}/inventories/locations`, data)
          .then(() => {
            this.goBack()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },
    deleteProduct (product) {
      this.products = this.products.filter(f => f.id !== product.id)
    },
    clearAutocomplete () {
      this.product = null
      this.searchProducts = null
      setTimeout(() => {
        this.productList = []
      }, 300)
    },
    goBack () {
      this.$router.push({ name: 'HQInventoryStorageLocations' })
    },
    hasStock (item) {
      if (!item.inventory_enabled) return true
      return item.inventory_qty > 0
    },
    getTypeIcon (item) {
      if (item.medication) return '$yzi-colors'
      if (item.type === 'documents') return '$yzi-file-06'
      return 'mdi-briefcase-outline'
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
  .autocmplt {
    .v-input__append-inner {
      margin-top: 12px !important;
    }
  }
}
.empty-box {
  position: relative;
  border-radius: 10px;
  border: var(--v-basic500) dashed 2px;
}
</style>
